<template>
  <div>
    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="livenessCheckData === null && !loadingLivenessCheck"
    >
      <h4 class="alert-heading">
        Error fetching liveness check data
      </h4>
      <div class="alert-body">
        No liveness check found with this ID. Check
        <b-link
          class="alert-link"
          :to="{ name: 'liveness-check-list'}"
        >
          Liveness Check List
        </b-link>
        for other records.
      </div>
    </b-alert>

    <b-card
      v-if="livenessCheckData"
      no-body
      class="mb-0"
    >
      <b-card-header>
        <h4 class="card-title">
          Liveness Check Details
        </h4>
      </b-card-header>

      <b-card-body>
        <b-overlay
          variant="white"
          spinner-variant="primary"
          blur="0"
          opacity=".75"
          rounded="sm"
          :show="loadingLivenessCheck"
        >
          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="Driver ID"
                label-for="driver-id"
              >
                <b-link
                  v-if="livenessCheckData.driverId"
                  :to="{
                    name: 'drivers-view',
                    params: { id: livenessCheckData.driverId },
                  }"
                  class="font-weight-bold"
                >
                  <feather-icon
                    icon="UserIcon"
                    size="16"
                    class="mr-1"
                  />
                  {{ livenessCheckData.driverId }}
                </b-link>
                <span v-else>
                  {{ livenessCheckData.driverId || 'N/A' }}
                </span>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="Status"
                label-for="status"
              >
                <b-badge
                  v-if="livenessCheckData.status"
                  pill
                  :variant="getStatusVariant(livenessCheckData.status)"
                  class="text-capitalize"
                >
                  {{ livenessCheckData.status }}
                </b-badge>
                <span v-else>N/A</span>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="Date & Time"
                label-for="date-time"
              >
                <span>{{ formatDateTime(livenessCheckData.createdAt) }}</span>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="ID"
                label-for="id"
              >
                <span>{{ livenessCheckData.id || 'N/A' }}</span>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Additional details if available -->
          <b-row
            v-if="livenessCheckData.details"
            class="mt-3"
          >
            <b-col cols="12">
              <b-form-group
                label="Additional Details"
                label-for="details"
              >
                <pre class="bg-light p-3 rounded">{{ JSON.stringify(livenessCheckData.details, null, 2) }}</pre>
              </b-form-group>
            </b-col>
          </b-row>
        </b-overlay>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BLink,
  BBadge,
  BOverlay,
  BAlert,
} from 'bootstrap-vue'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import livenessCheckStoreModule from './livenessCheckStoreModule'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BLink,
    BBadge,
    BOverlay,
    BAlert,
  },
  setup(props, ctx) {
    const LIVENESS_CHECK_APP_STORE_MODULE_NAME = 'livenessCheck'

    // Register module
    if (!store.hasModule(LIVENESS_CHECK_APP_STORE_MODULE_NAME)) {
      store.registerModule(LIVENESS_CHECK_APP_STORE_MODULE_NAME, livenessCheckStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(LIVENESS_CHECK_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(LIVENESS_CHECK_APP_STORE_MODULE_NAME)
      }
    })

    const livenessCheckData = ref(null)
    const loadingLivenessCheck = ref(false)

    const fetchLivenessCheck = async () => {
      loadingLivenessCheck.value = true
      try {
        const id = ctx.root.$route.params.id
        const data = await store.dispatch('livenessCheck/fetchLivenessCheckById', id)
        livenessCheckData.value = data
      } catch (error) {
        console.error('Error fetching liveness check:', error)
        livenessCheckData.value = null
      } finally {
        loadingLivenessCheck.value = false
      }
    }

    // Status variant helper
    const getStatusVariant = (status) => {
      const statusMap = {
        'VALID': 'success',
        'EXPIRED': 'danger',
        'PROCESSING': 'warning',
      }
      return statusMap[status] || 'secondary'
    }

    // Date formatting helper
    const formatDateTime = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleString()
    }

    // Fetch data on component mount
    fetchLivenessCheck()

    return {
      livenessCheckData,
      loadingLivenessCheck,
      getStatusVariant,
      formatDateTime,
    }
  },
}
</script>

<style scoped>
pre {
  font-size: 0.875rem;
  max-height: 300px;
  overflow-y: auto;
}
</style>
